/**
 * Phone Handler Utility
 * Shows confirmation dialog and copies phone number to clipboard to avoid WhatsApp conflicts
 */

// Import analytics tracking
import { trackCallNowClick } from './analytics';

/**
 * Copy text to clipboard with fallback for older browsers
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
const copyToClipboard = async (text) => {
  try {
    // Modern clipboard API (preferred)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (error) {
    console.error('Failed to copy phone number:', error);
    return false;
  }
};

/**
 * Handle phone call with confirmation dialog and clipboard copy
 * Prevents automatic opening of WhatsApp or other messaging apps
 * @param {string} phoneNumber - The phone number to call
 * @param {Event} event - The click event (optional)
 * @param {string} location - The location where the button was clicked (optional)
 */
export const handlePhoneCall = async (phoneNumber, event, location = 'unknown') => {
  // Prevent default behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Track the call now click with location data
  try {
    await trackCallNowClick(location, phoneNumber);
  } catch (trackError) {
    console.warn('Failed to track call now click:', trackError);
  }

  // Track Google Ads call conversion
  try {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-652712300/-izTCKeayOYbEOyynrcC'
      });
    }
  } catch (gtagError) {
    console.warn('Failed to track Google Ads conversion:', gtagError);
  }

  // Clean phone number (remove spaces, dashes, etc. but keep + for international format)
  const cleanNumber = phoneNumber.replace(/[\s-()]/g, '');
  
  try {
    // Try to open phone dialer directly
    window.location.href = `tel:${cleanNumber}`;
  } catch (error) {
    console.error('Error opening phone dialer:', error);
    // Fallback: copy to clipboard
    const copySuccess = await copyToClipboard(cleanNumber);
    
    if (copySuccess) {
      // eslint-disable-next-line no-alert
      window.alert(`✅ Phone number copied to clipboard!\n\n${phoneNumber}\n\nYou can now paste this number in your phone dialer to make the call.`);
    } else {
      // eslint-disable-next-line no-alert
      window.alert(`📱 MaxiCar Taxi Phone Number:\n\n${phoneNumber}\n\nPlease copy this number and paste it in your phone dialer to make the call.`);
    }
  }
};

/**
 * Create phone call event handler
 * @param {string} phoneNumber - The phone number to call
 * @returns {Function} Event handler function
 */
export const createPhoneCallHandler = (phoneNumber) => {
  return (event) => {
    handlePhoneCall(phoneNumber, event);
  };
};

/**
 * Add phone call handler to an element
 * @param {HTMLElement} element - The element to add the handler to
 * @param {string} phoneNumber - The phone number to call
 */
export const addPhoneCallHandler = (element, phoneNumber) => {
  if (element) {
    element.addEventListener('click', createPhoneCallHandler(phoneNumber));
  }
};

const phoneHandlerUtils = {
  handlePhoneCall,
  createPhoneCallHandler,
  addPhoneCallHandler
};

export default phoneHandlerUtils;